/**
 * Export utilities for client-side data export.
 * Uses a web worker for isolation and non-blocking UI.
 */

import { showToast } from '../web-components/toast/toast.js';
import { generateCsv, type CsvOptions } from './csv.js';

// Re-export for backward compatibility
export { generateCsv };
export type ExportOptions = CsvOptions;

export type ExportFormat = 'csv' | 'xlsx';

interface ExportResult {
  buffer: ArrayBuffer;
  filename: string;
  mimeType: string;
}

interface ExportError {
  error: string;
}

let exportWorker: Worker | null = null;

function getWorker(): Worker {
  if (!exportWorker) {
    exportWorker = new Worker('/bundles/workers.js', { type: 'module' });
  }
  return exportWorker;
}

/**
 * Export data to CSV or XLSX format via worker.
 * Returns a promise that resolves when download is triggered.
 */
export function exportData(
  rows: Record<string, unknown>[],
  filename: string,
  format: ExportFormat = 'csv',
  options: ExportOptions = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    const worker = getWorker();

    const handleMessage = (e: MessageEvent<ExportResult | ExportError>) => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);

      if ('error' in e.data) {
        showToast({ message: 'Export failed. Try again.', type: 'error' });
        reject(new Error(e.data.error));
        return;
      }

      // Create blob from ArrayBuffer received from worker
      const blob = new Blob([e.data.buffer], { type: e.data.mimeType });
      downloadBlob(blob, e.data.filename);
      showToast({ message: `Exported ${e.data.filename}`, type: 'success' });
      resolve();
    };

    const handleError = (err: ErrorEvent) => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      showToast({ message: 'Export failed. Try again.', type: 'error' });
      reject(new Error(err.message || 'Export worker error'));
    };

    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError);

    worker.postMessage({
      type: 'export:generate',
      format,
      rows,
      filename,
      columns: options.columns,
    });
  });
}

/**
 * Convenience function for CSV export.
 */
export function downloadCsv(
  rows: Record<string, unknown>[],
  filename: string,
  options: ExportOptions = {}
): Promise<void> {
  return exportData(rows, filename, 'csv', options);
}

/**
 * Convenience function for XLSX export.
 */
export function downloadXlsx(
  rows: Record<string, unknown>[],
  filename: string,
  options: ExportOptions = {}
): Promise<void> {
  return exportData(rows, filename, 'xlsx', options);
}

/**
 * Trigger browser download for a blob.
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

